// ============================================================================
// GST STAGING PREVIEW — G-1 slice 3b (ADR-0025 §4)
// ============================================================================
// What the parser read, row by row, with each row's classification. This is
// the user's first look at the data and the place to see what was NOT read as
// data and why: title rows, the header, totals. Nothing here is editable —
// staging is the verbatim record of what the client sent.
//
// Columns are the sheet's own header cells, so a billing package's rate-pivoted
// register shows as Taxable5% | Tax5% | … exactly as the client sees it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstSubmissionService } from 'api/services/gstSubmissionService';
import type { GstDataSubmission, GstEngagement, GstStagingRow } from 'api/types/gstEngagement.types';
import GstMappedPreview from './GstMappedPreview';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  submission: GstDataSubmission | null;
  /** Recording a mapping profile is a write; follows the `edit` grant. */
  canSelectProfile?: boolean;
  /** Accepting or rejecting a proposed profile; follows the `approve` grant. */
  canApproveProfile?: boolean;
  onClose: () => void;
}

type View = 'raw' | 'mapped';

const KIND_FILTERS = [
  { value: '', label: 'All rows' },
  { value: 'DATA', label: 'Data only' },
  { value: 'HEADER', label: 'Header' },
  { value: 'TITLE', label: 'Title rows' },
  { value: 'TRAILER', label: 'Totals / trailers' },
  { value: 'IGNORED', label: 'Ignored' },
];

const kindBadge = (kind: string) => {
  switch (kind) {
    case 'DATA': return { cls: 'badge badge-active', label: 'Data' };
    case 'HEADER': return { cls: 'badge badge-info', label: 'Header' };
    case 'CONTINUATION': return { cls: 'badge badge-info', label: 'Line' };
    case 'TRAILER': return { cls: 'badge badge-inactive', label: 'Total' };
    case 'TITLE': return { cls: 'badge badge-inactive', label: 'Title' };
    default: return { cls: 'badge badge-inactive', label: 'Ignored' };
  }
};

const cellText = (v: string | number | boolean | null, t: string): string => {
  if (v === null || v === undefined) return '';
  if (t === 'd' && typeof v === 'string') return v.slice(0, 10);
  if (t === 'n' && typeof v === 'number') return Number.isInteger(v) ? v.toString() : v.toFixed(2);
  if (t === 'b') return v ? 'TRUE' : 'FALSE';
  return String(v);
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const GstStagingPreviewModal: React.FC<Props> = ({ isOpen, engagement, submission, canSelectProfile = false, canApproveProfile = false, onClose }) => {
  const [view, setView] = useState<View>('raw');
  const [sheet, setSheet] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rows, setRows] = useState<GstStagingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const sheetStats = submission?.parseStats?.sheets ?? [];
  const current = sheetStats.find((s) => s.name === sheet) ?? sheetStats[0];

  useEffect(() => {
    if (isOpen && submission) {
      setView('raw');
      setSheet(submission.parseStats?.sheets[0]?.name ?? '');
      setKind('');
      setPage(1);
    }
  }, [isOpen, submission]);

  const load = useCallback(async () => {
    if (!engagement || !submission || !sheet) return;
    setLoading(true);
    try {
      const res = await gstSubmissionService.getStaging(engagement.id, submission.id, {
        page, pageSize, sheetName: sheet, rowKind: kind || undefined,
      });
      setRows(res.data);
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Could not load the staging rows.');
    } finally {
      setLoading(false);
    }
  }, [engagement, submission, sheet, kind, page, pageSize]);

  useEffect(() => { if (isOpen && view === 'raw') void load(); }, [isOpen, view, load]);

  // One column per header cell of the selected sheet; rows wider than the
  // header (a stray value to the right) get a numbered column so nothing is
  // hidden.
  const columns: SyutGridColumn<GstStagingRow>[] = useMemo(() => {
    const headers = current?.headerCells ?? [];
    const width = Math.max(headers.length, ...rows.map((r) => r.cells.length));
    const dynamic: SyutGridColumn<GstStagingRow>[] = [];
    for (let i = 0; i < width; i++) {
      const label = headers[i]?.trim() || `Col ${i + 1}`;
      dynamic.push({
        id: `c${i}`,
        header: label,
        size: 140,
        enableSorting: false,
        cell: ({ row }: any) => {
          const c = (row.original as GstStagingRow).cells[i];
          if (!c) return '';
          const text = cellText(c.v, c.t);
          return (
            <span
              title={c.t === 's' && /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/.test(text) ? 'A date typed as text' : undefined}
              style={{ textAlign: c.t === 'n' ? 'right' : 'left', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {text}
            </span>
          );
        },
      });
    }
    return [
      {
        id: 'rowIndex', header: '#', accessorKey: 'rowIndex', size: 64, enableSorting: false,
        cell: ({ row }: any) => <span style={{ color: 'var(--text-secondary)' }}>{row.original.rowIndex}</span>,
      },
      {
        id: 'rowKind', header: 'Kind', accessorKey: 'rowKind', size: 84, enableSorting: false,
        cell: ({ row }: any) => { const b = kindBadge(row.original.rowKind); return <span className={b.cls}>{b.label}</span>; },
      },
      ...dynamic,
    ];
  }, [current, rows]);

  if (!isOpen || !engagement || !submission) return null;

  const title = engagement.clientLegalName || engagement.clientName || 'GST engagement';
  const stats = submission.parseStats;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Staging preview — {submission.gstin ?? title} · {formatDate(submission.taxPeriodStart)} – {formatDate(submission.taxPeriodEnd)} · {submission.sourceRef} · rev {submission.submissionNo}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close"><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div style={{ display: 'flex', gap: 0, marginBottom: 10, borderBottom: '1px solid var(--border-color)' }}>
            {([['raw', 'Rows as received'], ['mapped', 'Mapped']] as [View, string][]).map(([v, label]) => (
              <button
                key={v}
                className="btn"
                onClick={() => setView(v)}
                style={{
                  fontSize: '0.8125rem', padding: '4px 14px', borderRadius: 0, background: 'transparent',
                  borderBottom: view === v ? '2px solid #2563eb' : '2px solid transparent',
                  color: view === v ? 'var(--text-primary)' : 'var(--text-secondary)', fontWeight: view === v ? 600 : 400,
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {view === 'mapped' && (
            <GstMappedPreview engagement={engagement} submission={submission} canSelect={canSelectProfile} canApprove={canApproveProfile} />
          )}

          {view === 'raw' && (<>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {sheetStats.map((s) => (
              <button
                key={s.name}
                className={`btn ${s.name === current?.name ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                onClick={() => { setSheet(s.name); setPage(1); }}
                title={`header row ${s.headerRow ?? '—'} · ${s.title} title · ${s.trailer} totals · ${s.ignored} ignored`}
              >
                {s.name} · {s.data.toLocaleString()} data
              </button>
            ))}
          </div>

          {current && (
            <div
              style={{
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.5rem 0.625rem', marginBottom: 10, fontSize: '0.75rem', color: 'var(--text-secondary)',
                display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center',
              }}
            >
              <span>
                Header on row <strong style={{ color: 'var(--text-primary)' }}>{current.headerRow ?? '—'}</strong> ·{' '}
                {current.data.toLocaleString()} data · {current.title} title · {current.trailer} totals · {current.ignored} ignored.
                {' '}Rows are shown exactly as the file has them; line items under an invoice are classified when a mapping profile is applied.
              </span>
              <span style={{ width: 200 }}>
                <SyutSelect
                  options={KIND_FILTERS}
                  value={kind}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => { setKind((v as string) || ''); setPage(1); }}
                />
              </span>
            </div>
          )}

          {stats && stats.data === 0 && (
            <div style={{ border: '1px solid #f59e0b', borderRadius: 6, padding: '0.625rem', marginBottom: 10, fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>No data rows were found.</strong>{' '}
              Either the sheet has no table, or the header was not where the classifier looked. The rows below show what it saw.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={rows as any}
              columns={columns as any}
              enablePagination
              manualPagination
              totalCount={total}
              currentPage={page - 1}
              pageSize={pageSize}
              onPageChange={(idx: number) => setPage(idx + 1)}
              onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
              emptyMessage={loading ? 'Loading…' : 'No rows.'}
            />
          </div>
          </>)}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default GstStagingPreviewModal;
